import { useCallback } from 'react';
import { ASSET_VERIFIED, type AssetVerified } from '@terra/ui-design-system';
import type {
  AssetsApiTypeDto,
  AssetsSearchBodyDto,
  UnverifiedAssetStatus,
} from '@terra/shared-contracts-assets';
import {
  DEFAULT_LIFECYCLE_STATE_FILTER,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';
import { createTabScopedStore } from './createTabScopedStore';

export const ASSET_TYPE = {
  ALL: 'ALL',
  ENDPOINTS: 'endpoints',
  WEB_ENDPOINTS: 'webEndpoints',
  WEB_PAGES: 'webpages',
  AGENTS: 'agents',
} as const;

export type AssetType = (typeof ASSET_TYPE)[keyof typeof ASSET_TYPE];

const CODE_GRAPH_FILTER_ASSET_TYPES: readonly AssetType[] = [
  ASSET_TYPE.ENDPOINTS,
  ASSET_TYPE.WEB_ENDPOINTS,
];

export const supportsCodeGraphFilter = (typeFilter: AssetType): boolean =>
  CODE_GRAPH_FILTER_ASSET_TYPES.includes(typeFilter);
export const HTTP_METHOD_VERBS = [
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
] as const;

export type HttpMethodVerb = (typeof HTTP_METHOD_VERBS)[number];

const HTTP_METHOD_VERB_SET = new Set<string>(HTTP_METHOD_VERBS);

export const normalizeMethodsFilter = (value: unknown): HttpMethodVerb[] => {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is HttpMethodVerb =>
        typeof item === 'string' && HTTP_METHOD_VERB_SET.has(item),
    );
  }

  if (
    typeof value === 'string' &&
    value !== 'ALL' &&
    HTTP_METHOD_VERB_SET.has(value)
  ) {
    return [value as HttpMethodVerb];
  }

  return [];
};

export type AssetsSecurityFilter = NonNullable<
  AssetsSearchBodyDto['endpointSecurityType']
>;
export type AssetsDetectionView = AssetsSearchBodyDto['detectionView'];

export type AssetsTabFilterState = {
  searchTerm: string;
  typeFilter: AssetType;
  securityFilter: AssetsSecurityFilter | null;
  detectionView: AssetsDetectionView;
  methodsFilter: HttpMethodVerb[];
  apiTypes: AssetsApiTypeDto[];
  withCodeGraph: boolean;
  verified: AssetVerified;
  unverifiedStatuses: UnverifiedAssetStatus[];
  createdStartDate: Date | null;
  createdEndDate: Date | null;
  createdSince: Date | null;
  updatedStartDate: Date | null;
  updatedEndDate: Date | null;
  lifecycleState: LifecycleStateFilterDto;
};

export const buildTypeFilterUpdate = (
  typeFilter: AssetType,
): Pick<AssetsTabFilterState, 'typeFilter'> &
  Partial<
    Pick<
      AssetsTabFilterState,
      | 'methodsFilter'
      | 'verified'
      | 'withCodeGraph'
      | 'unverifiedStatuses'
      | 'securityFilter'
    >
  > => ({
  typeFilter,
  ...(typeFilter !== ASSET_TYPE.ENDPOINTS ? { securityFilter: null } : {}),
  ...(typeFilter === ASSET_TYPE.WEB_PAGES || typeFilter === ASSET_TYPE.AGENTS
    ? {
        methodsFilter: [],
        verified: ASSET_VERIFIED.YES,
        unverifiedStatuses: [],
      }
    : {}),
  ...(!supportsCodeGraphFilter(typeFilter) ? { withCodeGraph: false } : {}),
});

const METHOD_FILTER_ASSET_TYPES: readonly AssetType[] = [
  ASSET_TYPE.ENDPOINTS,
  ASSET_TYPE.WEB_ENDPOINTS,
];

export const buildMethodsFilterUpdate = ({
  methodsFilter,
  typeFilter,
}: {
  methodsFilter: HttpMethodVerb[];
  typeFilter: AssetType;
}): Pick<AssetsTabFilterState, 'methodsFilter'> &
  Partial<Pick<AssetsTabFilterState, 'typeFilter'>> =>
  methodsFilter.length === 0 || METHOD_FILTER_ASSET_TYPES.includes(typeFilter)
    ? { methodsFilter }
    : { methodsFilter, typeFilter: ASSET_TYPE.ENDPOINTS };

export const ASSETS_VIEW = {
  ENDPOINTS: 'endpoints',
  WEB_PAGES: 'webpages',
  UNVERIFIED: 'unverified',
} as const;

export type AssetsView = (typeof ASSETS_VIEW)[keyof typeof ASSETS_VIEW];

const ASSETS_VIEW_FILTERS: Record<
  AssetsView,
  Pick<AssetsTabFilterState, 'typeFilter' | 'verified'>
> = {
  [ASSETS_VIEW.ENDPOINTS]: {
    typeFilter: ASSET_TYPE.ENDPOINTS,
    verified: ASSET_VERIFIED.YES,
  },
  [ASSETS_VIEW.WEB_PAGES]: {
    typeFilter: ASSET_TYPE.WEB_PAGES,
    verified: ASSET_VERIFIED.YES,
  },
  [ASSETS_VIEW.UNVERIFIED]: {
    typeFilter: ASSET_TYPE.ENDPOINTS,
    verified: ASSET_VERIFIED.NO,
  },
};

export const resolveAssetsView = ({
  typeFilter,
  verified,
}: Pick<AssetsTabFilterState, 'typeFilter' | 'verified'>): AssetsView => {
  if (verified === ASSET_VERIFIED.NO) {
    return ASSETS_VIEW.UNVERIFIED;
  }

  return typeFilter === ASSET_TYPE.WEB_PAGES
    ? ASSETS_VIEW.WEB_PAGES
    : ASSETS_VIEW.ENDPOINTS;
};

export const buildAssetsViewUpdate = (
  view: AssetsView,
): Partial<AssetsTabFilterState> => {
  const { typeFilter, verified } = ASSETS_VIEW_FILTERS[view];

  return {
    ...buildTypeFilterUpdate(typeFilter),
    securityFilter: null,
    ...(view !== ASSETS_VIEW.ENDPOINTS ? { apiTypes: [] } : {}),
    verified,
    ...(verified === ASSET_VERIFIED.YES ? { unverifiedStatuses: [] } : {}),
  };
};

const useAssetsTabScopedFilterStoreBase =
  createTabScopedStore<AssetsTabFilterState>({
    searchTerm: '',
    typeFilter: ASSET_TYPE.ENDPOINTS,
    securityFilter: null,
    detectionView: 'active',
    methodsFilter: [],
    apiTypes: [],
    withCodeGraph: false,
    verified: ASSET_VERIFIED.YES,
    unverifiedStatuses: [],
    createdStartDate: null,
    createdEndDate: null,
    createdSince: null,
    updatedStartDate: null,
    updatedEndDate: null,
    lifecycleState: DEFAULT_LIFECYCLE_STATE_FILTER,
  });

export function useTabScopedAssetsFilters() {
  const { filters, update, reset, clearTabScope } =
    useAssetsTabScopedFilterStoreBase();

  const setSearchTerm = useCallback(
    (searchTerm: string) => update({ searchTerm }),
    [update],
  );
  const setTypeFilter = useCallback(
    (typeFilter: AssetType) => update(buildTypeFilterUpdate(typeFilter)),
    [update],
  );
  const setEndpointSegment = useCallback(
    (securityFilter: AssetsSecurityFilter | null) =>
      update({
        ...buildTypeFilterUpdate(ASSET_TYPE.ENDPOINTS),
        securityFilter,
      }),
    [update],
  );
  const setDetectionView = useCallback(
    (detectionView: AssetsDetectionView) => update({ detectionView }),
    [update],
  );
  const setView = useCallback(
    (view: AssetsView) => update(buildAssetsViewUpdate(view)),
    [update],
  );
  const { detectionView, lifecycleState } = filters;
  const resetFiltersForView = useCallback(
    (view: AssetsView) => {
      reset();
      update({ ...buildAssetsViewUpdate(view), detectionView, lifecycleState });
    },
    [detectionView, lifecycleState, reset, update],
  );
  const setVerified = useCallback(
    (verified: AssetVerified) =>
      update({
        verified,
        ...(verified === ASSET_VERIFIED.YES ? { unverifiedStatuses: [] } : {}),
      }),
    [update],
  );
  const setUnverifiedStatuses = useCallback(
    (unverifiedStatuses: UnverifiedAssetStatus[]) =>
      update({ unverifiedStatuses }),
    [update],
  );
  const methodsFilter = normalizeMethodsFilter(filters.methodsFilter);
  const { typeFilter } = filters;
  const setMethodsFilter = useCallback(
    (nextMethodsFilter: HttpMethodVerb[]) =>
      update(
        buildMethodsFilterUpdate({
          methodsFilter: normalizeMethodsFilter(nextMethodsFilter),
          typeFilter,
        }),
      ),
    [typeFilter, update],
  );
  const setApiTypes = useCallback(
    (apiTypes: AssetsApiTypeDto[]) => update({ apiTypes }),
    [update],
  );
  const setWithCodeGraph = useCallback(
    (withCodeGraph: boolean) => update({ withCodeGraph }),
    [update],
  );
  const setCreatedStartDate = useCallback(
    (createdStartDate: Date | null) => update({ createdStartDate }),
    [update],
  );
  const setCreatedEndDate = useCallback(
    (createdEndDate: Date | null) => update({ createdEndDate }),
    [update],
  );
  const setCreatedSince = useCallback(
    (createdSince: Date | null) => update({ createdSince }),
    [update],
  );
  const setUpdatedStartDate = useCallback(
    (updatedStartDate: Date | null) => update({ updatedStartDate }),
    [update],
  );
  const setUpdatedEndDate = useCallback(
    (updatedEndDate: Date | null) => update({ updatedEndDate }),
    [update],
  );
  const setLifecycleState = useCallback(
    (lifecycleState: LifecycleStateFilterDto) => update({ lifecycleState }),
    [update],
  );

  return {
    ...filters,
    methodsFilter,
    view: resolveAssetsView(filters),
    setView,
    resetFiltersForView,
    setSearchTerm,
    setTypeFilter,
    setEndpointSegment,
    setDetectionView,
    setVerified,
    setUnverifiedStatuses,
    setMethodsFilter,
    setApiTypes,
    setWithCodeGraph,
    setCreatedStartDate,
    setCreatedEndDate,
    setCreatedSince,
    setUpdatedStartDate,
    setUpdatedEndDate,
    setLifecycleState,
    resetFilters: reset,
    clearTabScope,
  };
}
